import React, { FC, useCallback, useState } from 'react'
import styles from 'styles/components/accordion.module.scss'
import format from 'date-fns/format'
import { changeCouponStatus, getExpiredDate } from 'reducks/coupon/operations'
import { CouponType } from 'types/coupon'
import { useQueryCouponUsers } from 'hooks/coupon'
import { COUPON_DISCOUNT_TYPE, COUPON_STATUS } from 'constants/coupon'
import { StoreType } from 'types/store'
import { Spacer } from 'components/Utility'
import { CautionButton, GreyButton, OrangeButton } from 'components/Button'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import { addSeconds } from 'date-fns'
import { parseSecondsToDays } from 'lib/helper'
import { openCautionModal } from 'reducks/modal/operations'

type Props = {
  coupon: CouponType
  isExpired: boolean
  stores: StoreType[]
}

const CouponAccordion: FC<Props> = (props) => {
  const { coupon, isExpired, stores } = props
  const dispatch = useDispatch()
  const goToCouponLinkPage = useCallback(
    () => dispatch(push(`/admin/coupon/list/${coupon.coupon_id}`)),
    [coupon.coupon_id]
  )
  const goToCouponUsersPage = useCallback(
    () => dispatch(push(`/admin/coupon/${coupon.coupon_id}/user`)),
    [coupon.coupon_id]
  )
  const [open, setOpen] = useState<boolean>(false)
  const toggleOpen = useCallback(() => setOpen((prevState) => !prevState), [setOpen])
  const couponUsers = useQueryCouponUsers(coupon.coupon_id || '')
  const conditionOfUse = (): string => {
    const conditions: string[] = []
    if (coupon.limitations.is_first) conditions.push('初回購入限定')
    if (coupon.type === 'manual') conditions.push('限定したユーザーへ配布')
    if (!coupon.limitations.is_first && coupon.type !== 'manual') conditions.push('制限無し')
    return conditions.join('、')
  }
  const openStopModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '配布停止',
        'キャンセル',
        '既に配布されたクーポンは引き続き利用することができます。',
        () => dispatch(changeCouponStatus(coupon.coupon_id || '', 'private')),
        'クーポンの配布を停止します'
      )
    )
  }, [coupon])

  const openRestartModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '配布再開',
        'キャンセル',
        '既に配布されたクーポンは引き続き利用することができます。',
        () => dispatch(changeCouponStatus(coupon.coupon_id || '', 'public')),
        'クーポンの配布を再開します'
      )
    )
  }, [coupon])

  return (
    <div className={open ? styles.open : ''}>
      <div className={styles.coupon}>
        <div className={styles.coupon__header} onClick={toggleOpen}>
          <div className={styles.coupon__description}>
            <h2 className={isExpired ? styles.coupon__title_expired : styles.coupon__title}>{coupon.name}</h2>
            <p>
              クーポンコード : <span className={styles.coupon__code}>{coupon.code}</span>
            </p>
            <p>
              利用期限 :
              {!coupon.expires_at && !coupon.expires_maxage
                ? '期限無し'
                : format(getExpiredDate(coupon.expires_at || undefined, coupon.expires_maxage || 0), 'yyyy年MM月dd日')}
            </p>
            <p>取得済み : {couponUsers.length.toLocaleString()}人</p>
            <span className={styles.coupon__arrow} />
          </div>
        </div>
        <div className={styles.coupon__collapsedContent}>
          <div className={styles.coupon__description}>
            <p>ステータス : {COUPON_STATUS[coupon.status]}</p>
            <p>
              割引方法 :&nbsp;
              {coupon.discount_amount > 0 ? COUPON_DISCOUNT_TYPE['flat_rate'] : COUPON_DISCOUNT_TYPE['percent']}
            </p>
            <p>
              割引金額 :&nbsp;
              {coupon.discount_amount > 0
                ? `${coupon.discount_amount.toLocaleString()}円`
                : `${coupon.discount_rate.toLocaleString()}%`}
            </p>
            <p>最小決済金額 : {(coupon.limitations.min_amount || 0).toLocaleString()}円</p>
            <p>配布上限 : {coupon.limitations.max_total ? coupon.limitations.max_total.toLocaleString() : '無制限'}</p>
            <p>取得条件 : {conditionOfUse()}</p>
            <p>固定期限 : {coupon.expires_at ? format(coupon.expires_at.toDate(), 'yyyy年MM月dd日') : '無し'}</p>
            <p>相対期限 : {coupon.expires_maxage ? parseSecondsToDays(coupon.expires_maxage) + '日後' : '無し'}</p>
            <p>
              利用可能回数 :&nbsp;
              {coupon.limitations.max_per_user ? `${coupon.limitations.max_per_user.toLocaleString()}回` : '制限無し'}
            </p>
            <p>
              利用可能店舗 :&nbsp;
              {coupon.targets && coupon.targets.stores && coupon.targets.stores.length > 0
                ? coupon.targets.stores
                    .map((storeId) => {
                      const store = stores.find((store) => store.store_id && store.store_id === storeId)
                      if (!store) return ''
                      return store.name
                    })
                    .join(`、`)
                : 'どの店舗でも利用可能'}
            </p>
          </div>
          <Spacer size={12} />
          {!isExpired && coupon.status === 'public' && (
            <>
              <OrangeButton label={'ユーザーへ付与する'} onClick={goToCouponLinkPage} size={'medium'} type={'main'} />
              <Spacer size={12} />
            </>
          )}
          <CautionButton
            label={coupon.status === 'public' ? '配布停止' : '配布再開'}
            onClick={coupon.status === 'public' ? openStopModal : openRestartModal}
            size={'medium'}
            type={'outlined'}
          />
          <Spacer size={12} />
          <GreyButton label={'配布済みユーザー一覧'} onClick={goToCouponUsersPage} size={'medium'} type={'outlined'} />
        </div>
      </div>
    </div>
  )
}

export default CouponAccordion
